import { AxiosError, AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Cloudflare Turnstile tokens for the anonymous backend calls that cost money or create records.
 *
 * On 23-25/10/2026 a script replayed the onboarding OTP call ~10,700 times (SMS pumping, ~2,300 EUR).
 * The backend (`RestWorld.BotChallenge` in core-services) now asks those calls for a token that only a
 * browser running the Turnstile widget on one of our pages can mint. This module mints it and the Axios
 * interceptor in `axios.ts` attaches it, so no call site has to know about it.
 *
 * - The token travels in the `x-bot-challenge` header, minted for the action the backend expects on that
 *   endpoint (`BOT_CHALLENGE_ENDPOINTS`). A token is single-use: one per request.
 * - Without `NEXT_PUBLIC_TURNSTILE_SITE_KEY`, or on the server (SSR), nothing happens.
 * - The Cloudflare script is loaded on the first protected call, never on page load.
 * - If the widget does not answer within `TOKEN_TIMEOUT_MS`, the request goes without a token: the backend
 *   logs it and, while the action is in `log` mode, lets it through. Never blocking a person on our side is
 *   deliberate; the backend decides.
 * - A 403 with `code: "bot_challenge_failed"` (action in `enforce`) is retried once with a fresh token.
 */

export const BOT_CHALLENGE_HEADER = 'x-bot-challenge';
const FAILED_CODE = 'bot_challenge_failed';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 8000;

/** Method + path of each protected endpoint, and the action name the backend checks the token against. */
export const BOT_CHALLENGE_ENDPOINTS: ReadonlyArray<{ method: string; path: string; action: string }> = [
  { method: 'get', path: '/accounts/verification_phone_otp', action: 'otp_request' },
  { method: 'post', path: '/accounts/verify_phone_otp', action: 'otp_verify' },
  { method: 'post', path: '/workers/upload_cv', action: 'cv_upload' },
  { method: 'post', path: '/workers/cv', action: 'cv_missing_data' },
  { method: 'post', path: '/workers/worker', action: 'worker_create' },
  { method: 'post', path: '/organizations', action: 'organization_create' },
  { method: 'put', path: '/organizations/prospects', action: 'prospect_upsert' },
  { method: 'post', path: '/employers/job_positions/generate_ad_content', action: 'ad_generate' },
  { method: 'post', path: '/employers/job_positions/validate_ad', action: 'ad_validate' },
  { method: 'get', path: '/employers/restaurants/additional_information', action: 'venue_information' },
];

type TurnstileRenderOptions = {
  sitekey: string;
  action: string;
  callback: (token: string) => void;
  'error-callback': () => void;
  'timeout-callback': () => void;
  'refresh-expired': 'never';
  retry: 'never';
};

type Turnstile = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

const siteKey = () => process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

/** Whether this page can mint tokens at all. */
export const isBotChallengeEnabled = () => typeof window !== 'undefined' && !!siteKey();

const pathOf = (url: string | undefined) => {
  if (!url) return '';
  try {
    return new URL(url, 'http://relative.invalid').pathname.replace(/\/+$/, '');
  } catch {
    return url.split('?')[0].replace(/\/+$/, '');
  }
};

/** The action a request needs a token for, or undefined when the endpoint is not protected. */
export const botChallengeActionFor = (method: string | undefined, url: string | undefined) => {
  const path = pathOf(url);
  return BOT_CHALLENGE_ENDPOINTS.find(
    (endpoint) => endpoint.method === (method || 'get').toLowerCase() && endpoint.path === path
  )?.action;
};

let scriptPromise: Promise<Turnstile | undefined> | undefined;

/** Loads the Cloudflare script once. Also used by `useBotChallenge` to warm it up on the funnels. */
export const loadTurnstile = (): Promise<Turnstile | undefined> => {
  if (!isBotChallengeEnabled()) return Promise.resolve(undefined);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    // Iubenda autoblocking: purpose 1 = strictly necessary, so the consent banner never holds it back.
    script.setAttribute('data-iub-purposes', '1');
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      scriptPromise = undefined;
      resolve(undefined);
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

const withTimeout = <T>(promise: Promise<T>, fallback: T, ms: number) =>
  Promise.race([promise, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))]);

const mintToken = async (action: string): Promise<string | undefined> => {
  const turnstile = await loadTurnstile();
  const sitekey = siteKey();
  if (!turnstile || !sitekey) return undefined;

  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);
  let widgetId: string | undefined;

  try {
    return await new Promise<string | undefined>((resolve) => {
      widgetId = turnstile.render(container, {
        sitekey,
        action,
        callback: (value) => resolve(value),
        'error-callback': () => resolve(undefined),
        'timeout-callback': () => resolve(undefined),
        'refresh-expired': 'never',
        retry: 'never',
      });
    });
  } finally {
    if (widgetId) turnstile.remove(widgetId);
    container.remove();
  }
};

/**
 * Mints one token for `action`, or resolves undefined when it cannot (no key, SSR, widget failure). One deadline
 * covers the script download and the widget together, so a protected call never waits more than
 * `TOKEN_TIMEOUT_MS` for its token; a widget still running past it cleans itself up when it answers.
 */
export const getBotChallengeToken = (action: string): Promise<string | undefined> =>
  withTimeout(mintToken(action), undefined, TOKEN_TIMEOUT_MS);

type RetriableConfig = InternalAxiosRequestConfig & { botChallengeRetried?: boolean };

const attachToken = async (config: InternalAxiosRequestConfig, action: string) => {
  const token = await getBotChallengeToken(action);
  if (token && config.headers) (config.headers as AxiosHeaders).set(BOT_CHALLENGE_HEADER, token);
  return config;
};

/** Request interceptor: a fresh token on every protected call. */
export const botChallengeRequestInterceptor = async (config: InternalAxiosRequestConfig) => {
  if (!isBotChallengeEnabled()) return config;
  const action = botChallengeActionFor(config.method, config.url);
  return action ? attachToken(config, action) : config;
};

/**
 * Response error handler: when the backend refused the token (expired, already spent, widget glitch), replay
 * the request once with a new one. Returns undefined when the error is not ours to handle.
 */
export const retryOnBotChallengeFailure = async (instance: AxiosInstance, error: AxiosError) => {
  const config = error.config as RetriableConfig | undefined;
  const data = error.response?.data as { code?: string } | undefined;
  if (!config || error.response?.status !== 403 || data?.code !== FAILED_CODE || config.botChallengeRetried)
    return undefined;

  const action = botChallengeActionFor(config.method, config.url);
  if (!action || !isBotChallengeEnabled()) return undefined;

  // The request interceptor mints the fresh token when the request goes through the instance again.
  config.botChallengeRetried = true;
  (config.headers as AxiosHeaders | undefined)?.delete?.(BOT_CHALLENGE_HEADER);
  return instance.request(config);
};
